import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Newspaper, Rss, RefreshCw, Sparkles, Loader2, Check, X, ExternalLink,
  Plus, Pause, Play, AlertCircle, Zap, Radio, Globe, Languages,
  Pencil, Trash2, MoreVertical, ChevronDown, ChevronUp, Filter, History, Send,
  ArrowRight, LayoutGrid, Database, Music, Share2, Copy as CopyIcon, Bell, Moon, Mic, Square,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PlatformIcon, PlatformToggle, getPlatformMeta } from "@/components/PlatformIcon";
import useCollapsible from "@/hooks/useCollapsible";
import { appConfirm } from "@/components/AppDialogHost";

// Theme mirrors the platform-wide palette used by PodcastCreator /
// QuickPromoCreator. Also aligns with the spec's `#0f1117 / #1a1e2a
// / #4a9eff` requirements.
const T = {
  bg:       "#0f1117",
  card:     "#1a1e2a",
  elevated: "#161923",
  border:   "#232632",
  primary:  "#4a9eff",
  purple:   "#7c5cff",
  success:  "#3ddc97",
  warning:  "#ffb84a",
  danger:   "#ef5a6f",
  text:     "#e6e8ee",
  muted:    "#8b93a7",
  faint:    "#5a6178",
};

const COUNTRIES = [
  { code: "us", label: "United States" },
  { code: "gb", label: "United Kingdom" },
  { code: "ca", label: "Canada" },
  { code: "au", label: "Australia" },
  // ── Americas & Caribbean ──
  { code: "ar", label: "Argentina" },
  { code: "bs", label: "Bahamas" },
  { code: "bb", label: "Barbados" },
  { code: "bm", label: "Bermuda" },
  { code: "br", label: "Brazil" },
  { code: "cl", label: "Chile" },
  { code: "co", label: "Colombia" },
  { code: "cr", label: "Costa Rica" },
  { code: "cu", label: "Cuba" },
  { code: "do", label: "Dominican Republic" },
  { code: "ec", label: "Ecuador" },
  { code: "ht", label: "Haiti" },
  { code: "jm", label: "Jamaica" },
  { code: "mx", label: "Mexico" },
  { code: "pa", label: "Panama" },
  { code: "pe", label: "Peru" },
  { code: "pr", label: "Puerto Rico" },
  { code: "tt", label: "Trinidad & Tobago" },
  { code: "ve", label: "Venezuela" },
  // ── Europe ──
  { code: "at", label: "Austria" },
  { code: "be", label: "Belgium" },
  { code: "cz", label: "Czechia" },
  { code: "dk", label: "Denmark" },
  { code: "fi", label: "Finland" },
  { code: "fr", label: "France" },
  { code: "de", label: "Germany" },
  { code: "gr", label: "Greece" },
  { code: "hu", label: "Hungary" },
  { code: "ie", label: "Ireland" },
  { code: "it", label: "Italy" },
  { code: "nl", label: "Netherlands" },
  { code: "no", label: "Norway" },
  { code: "pl", label: "Poland" },
  { code: "pt", label: "Portugal" },
  { code: "ro", label: "Romania" },
  { code: "es", label: "Spain" },
  { code: "se", label: "Sweden" },
  { code: "ch", label: "Switzerland" },
  { code: "tr", label: "Turkey" },
  { code: "ua", label: "Ukraine" },
  // ── Africa & Middle East ──
  { code: "eg", label: "Egypt" },
  { code: "et", label: "Ethiopia" },
  { code: "gh", label: "Ghana" },
  { code: "il", label: "Israel" },
  { code: "ke", label: "Kenya" },
  { code: "ma", label: "Morocco" },
  { code: "ng", label: "Nigeria" },
  { code: "sa", label: "Saudi Arabia" },
  { code: "za", label: "South Africa" },
  { code: "tz", label: "Tanzania" },
  { code: "ug", label: "Uganda" },
  { code: "ae", label: "United Arab Emirates" },
  // ── Asia-Pacific ──
  { code: "bd", label: "Bangladesh" },
  { code: "cn", label: "China" },
  { code: "hk", label: "Hong Kong" },
  { code: "in", label: "India" },
  { code: "id", label: "Indonesia" },
  { code: "jp", label: "Japan" },
  { code: "my", label: "Malaysia" },
  { code: "nz", label: "New Zealand" },
  { code: "pk", label: "Pakistan" },
  { code: "ph", label: "Philippines" },
  { code: "sg", label: "Singapore" },
  { code: "kr", label: "South Korea" },
  { code: "tw", label: "Taiwan" },
  { code: "th", label: "Thailand" },
  { code: "vn", label: "Vietnam" },
];

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "pt", label: "Portuguese" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
]; // static fallback — replaced by the Global Language Center list at runtime

const CATEGORY_SUGGESTIONS = ["Local", "Weather", "Business", "Sports",
                              "Politics", "Entertainment", "Technology",
                              "Health", "Science"];
// Update #180 · Music genre suggestions — mirror the spec's Market
// Settings row. Kept separate from CATEGORY_SUGGESTIONS because the
// backend treats genres as an independent filter.
const GENRE_SUGGESTIONS = ["R&B", "Hip-Hop", "Pop", "Country", "Latin",
                           "EDM", "Gospel", "Reggae", "Rock", "Disco", "Jazz"];

const FILTERS = [
  { id: "all",      label: "All Sources", icon: LayoutGrid },
  { id: "provider", label: "Provider",    icon: Database   },
  { id: "rss",      label: "RSS",         icon: Rss        },
  { id: "breaking", label: "Breaking",    icon: Zap        },
  { id: "genre",    label: "Genre",       icon: Music      },
];

// Tiny favicon for a source URL — lets users tell sources apart at a glance.
function SourceFavicon({ url, size = 14, style }) {
  const [failed, setFailed] = useState(false);
  let domain = null;
  try { domain = new URL(url).hostname; } catch { /* not a valid url */ }
  if (!domain || failed) {
    return <Globe size={size - 2} style={{ color: "#6b7280", flexShrink: 0, ...style }} aria-hidden="true" />;
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      onError={() => setFailed(true)}
      style={{ borderRadius: 4, flexShrink: 0, ...style }}
    />
  );
}

export default function NewsIngestion() {
  const navigate = useNavigate();
  // ── State ──────────────────────────────────────────────────────
  const [settings, setSettings] = useState(null);
  const [sources,  setSources]  = useState([]);
  const [items,    setItems]    = useState([]);
  const [providers, setProviders] = useState([]);
  const [filter,   setFilter]   = useState("all");
  const [shareItem, setShareItem] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Add-feed row
  const [rssUrl,        setRssUrl] = useState("");
  const [addingSource,  setAddingSource] = useState(false);

  // Custom-category input
  const [newCategory, setNewCategory] = useState("");
  // Custom-genre input (Update #180)
  const [newGenre, setNewGenre] = useState("");
  // Suggested genre feeds (Update #180)
  const [suggested, setSuggested] = useState([]);

  // Script-gen modal
  const [scriptModal, setScriptModal] = useState(null); // {item, script, attribution, loading, ...}
  const [sendingToStudio, setSendingToStudio] = useState(false);

  // Script History vault modal
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyToneFilter, setHistoryToneFilter] = useState("");   // "" = all

  // Update #182 · Hot Takes state
  const [hotTakes, setHotTakes] = useState([]);
  const [hotPublishing, setHotPublishing] = useState(null);   // hot_take_id

  // ── Data loading ───────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    try {
      const [sRes, srcRes, itRes, pRes, htRes] = await Promise.all([
        api.get("/news-ingestion/settings"),
        api.get("/news-ingestion/sources"),
        api.get(`/news-ingestion/items?filter=${filter}&limit=30`),
        api.get("/news-ingestion/providers/status"),
        api.get("/news-ingestion/hot-takes?limit=6").catch(() => ({ data: { items: [] } })),
      ]);
      setSettings(sRes.data);
      setSources(srcRes.data.items || []);
      setItems(itRes.data.items || []);
      setLastRefresh(itRes.data.last_refreshed);
      setProviders(pRes.data.providers || []);
      setHotTakes((htRes.data.items || []).filter((h) => h.status !== "dismissed"));
    } catch (e) {
      toast.error("Could not load News Ingestion — try refreshing the page");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Update #190 · Primary Language synced with the Global Language Center
  const [langOptions, setLangOptions] = useState(LANGUAGES);
  useEffect(() => {
    api.get("/i18n/languages")
      .then((r) => {
        const all = r.data?.all || [];
        if (all.length) {
          setLangOptions(all.map((l) => ({
            code: l.code,
            label: `${l.flag ? l.flag + " " : ""}${l.name}${l.native && l.native !== l.name ? ` · ${l.native}` : ""}`,
          })));
        }
      })
      .catch(() => {});
  }, []);

  // Poll items every 60s so "Live · updated N min ago" stays fresh
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/news-ingestion/items?filter=${filter}&limit=30`);
        setItems(r.data.items || []);
        setLastRefresh(r.data.last_refreshed);
      } catch { /* silent */ }
    }, 60000);
    return () => clearInterval(t);
  }, [filter]);

  // ── Settings save (with Saved ✓ indicator) ────────────────────
  const [saveState, setSaveState] = useState(null); // null | 'saving' | 'saved'
  const saveTimer = useRef(null);
  const saveSettings = useCallback(async (partial) => {
    if (!settings) return;
    const merged = { ...settings, ...partial };
    setSettings(merged);
    setSaveState("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    try {
      const r = await api.put("/news-ingestion/settings", {
        country: merged.country,
        language: merged.language,
        categories: merged.categories,
        genres: merged.genres,
        auto_refresh_enabled: merged.auto_refresh_enabled,
        refresh_interval_minutes: merged.refresh_interval_minutes,
        provider_preference: merged.provider_preference,
        hot_take_autobroadcast: merged.hot_take_autobroadcast,
        hot_take_wp_publish: merged.hot_take_wp_publish,
        hot_take_wp_status: merged.hot_take_wp_status,
        hot_take_alert_email: merged.hot_take_alert_email,
        hot_take_quiet_enabled: merged.hot_take_quiet_enabled,
        hot_take_quiet_start: merged.hot_take_quiet_start,
        hot_take_quiet_end: merged.hot_take_quiet_end,
        hot_take_voice: merged.hot_take_voice,
        hot_take_voice_style: merged.hot_take_voice_style,
      });
      setSettings(r.data);
      setSaveState("saved");
      saveTimer.current = setTimeout(() => setSaveState(null), 2000);
    } catch (e) {
      setSaveState(null);
      toast.error(e?.response?.data?.detail || "Could not save settings");
    }
  }, [settings]);

  // ── RSS: add / pause / delete ─────────────────────────────────
  const addRssSource = useCallback(async () => {
    const url = rssUrl.trim();
    if (!url) return;
    setAddingSource(true);
    try {
      const r = await api.post("/news-ingestion/sources", { url });
      setSources((prev) => [...prev, r.data]);
      setRssUrl("");
      toast.success(`Added "${r.data.label}" — ${r.data.last_item_count} items pulled`);
      // Refresh items list so the new feed shows up right away
      const items = await api.get(`/news-ingestion/items?filter=${filter}&limit=30`);
      setItems(items.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feed could not be added");
    } finally {
      setAddingSource(false);
    }
  }, [rssUrl, filter]);

  const togglePause = useCallback(async (source) => {
    try {
      const r = await api.patch(`/news-ingestion/sources/${source.source_id}`, {
        paused: !source.paused,
      });
      setSources((prev) => prev.map((s) => (s.source_id === source.source_id ? r.data : s)));
      toast.success(r.data.paused ? "Feed paused" : "Feed resumed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  }, []);

  const deleteSource = useCallback(async (source) => {
    if (!await appConfirm(`Delete "${source.label}"? Its cached items will be removed.`)) return;
    try {
      await api.delete(`/news-ingestion/sources/${source.source_id}`);
      setSources((prev) => prev.filter((s) => s.source_id !== source.source_id));
      // Also drop that source's items from the visible list
      setItems((prev) => prev.filter((i) => i.source_id !== source.source_id));
      toast.success("Feed deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }, []);

  // ── Manual refresh ──────────────────────────────────────────
  const refreshNow = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/news-ingestion/refresh");
      if (r.data.provider_error) {
        toast.warning(`Provider: ${r.data.provider_error}`, { duration: 4000 });
      } else if (r.data.hot_takes_new > 0) {
        toast.success(`🔥 ${r.data.hot_takes_new} new Genre Hot Take${r.data.hot_takes_new === 1 ? "" : "s"} detected!`, { duration: 6000 });
      } else {
        toast.success(`Refreshed — ${r.data.inserted} new item${r.data.inserted === 1 ? "" : "s"}`);
      }
      const items = await api.get(`/news-ingestion/items?filter=${filter}&limit=30`);
      setItems(items.data.items || []);
      setLastRefresh(items.data.last_refreshed);
      // Also reload sources + hot takes so status dots update
      const [srcRes, htRes] = await Promise.all([
        api.get("/news-ingestion/sources"),
        api.get("/news-ingestion/hot-takes?limit=6").catch(() => ({ data: { items: [] } })),
      ]);
      setSources(srcRes.data.items || []);
      setHotTakes((htRes.data.items || []).filter((h) => h.status !== "dismissed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  }, [filter]);

  // ── Hot Take handlers (Update #182) ─────────────────────────
  const publishHotTake = useCallback(async (hotTakeId) => {
    setHotPublishing(hotTakeId);
    try {
      const r = await api.post(`/news-ingestion/hot-takes/${hotTakeId}/publish`);
      toast.success(r.data.already_published
        ? "Hot take was already published."
        : "Hot take promoted → News Story draft created.");
      const htRes = await api.get("/news-ingestion/hot-takes?limit=6");
      setHotTakes((htRes.data.items || []).filter((h) => h.status !== "dismissed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to publish");
    } finally { setHotPublishing(null); }
  }, []);

  const dismissHotTake = useCallback(async (hotTakeId) => {
    setHotTakes((prev) => prev.filter((h) => h.hot_take_id !== hotTakeId));
    try { await api.delete(`/news-ingestion/hot-takes/${hotTakeId}`); }
    catch { /* silent — UI already updated optimistically */ }
  }, []);

  // Update #261 · One-click air from email deep-link (?air=hot_xxx)
  const airedFromLink = useRef(false);
  useEffect(() => {
    if (airedFromLink.current) return;
    airedFromLink.current = true;
    const airId = new URLSearchParams(window.location.search).get("air");
    if (airId) {
      window.history.replaceState({}, "", window.location.pathname);
      publishHotTake(airId);
    }
  }, [publishHotTake]);

  // ── Category management ───────────────────────────────────
  const addCategory = useCallback(() => {
    const c = newCategory.trim();
    if (!c) return;
    const cats = settings?.categories || [];
    if (cats.some((x) => x.toLowerCase() === c.toLowerCase())) {
      toast.error("Category already added");
      return;
    }
    saveSettings({ categories: [...cats, c] });
    setNewCategory("");
  }, [newCategory, settings, saveSettings]);

  const removeCategory = useCallback((cat) => {
    const cats = (settings?.categories || []).filter((c) => c !== cat);
    saveSettings({ categories: cats });
  }, [settings, saveSettings]);

  // ── Genre management (Update #180 — spec v2 first-class filter) ──
  const loadSuggested = useCallback(async () => {
    try {
      const r = await api.get("/news-ingestion/suggested-genre-feeds");
      setSuggested(r.data.items || []);
    } catch { /* silent — non-critical */ }
  }, []);

  // Kickstart — merge genres server-side + auto-wire their curated feeds
  const kickstartGenres = useCallback(async (genres) => {
    if (!genres || genres.length === 0) return;
    const tid = toast.loading(`Wiring curated ${genres.join(", ")} feeds…`);
    try {
      const r = await api.post("/news-ingestion/genres/kickstart", { genres });
      const n = (r.data.added || []).length;
      if (n > 0) {
        toast.success(`🔥 Wired ${n} curated feed${n === 1 ? "" : "s"} — Hot Takes can now trigger`, { id: tid, duration: 6000 });
      } else {
        toast.success("All curated feeds for those genres are already wired ✓", { id: tid });
      }
      setSettings((s) => (s ? { ...s, genres: r.data.genres || s.genres } : s));
      const srcRes = await api.get("/news-ingestion/sources");
      setSources(srcRes.data.items || []);
      loadSuggested();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't wire suggested feeds", { id: tid });
    }
  }, [loadSuggested]);

  const addGenre = useCallback(() => {
    const g = newGenre.trim();
    if (!g) return;
    const gs = settings?.genres || [];
    if (gs.some((x) => x.toLowerCase() === g.toLowerCase())) {
      toast.error("Genre already added"); return;
    }
    setSettings((s) => (s ? { ...s, genres: [...gs, g] } : s));
    kickstartGenres([g]);
    setNewGenre("");
  }, [newGenre, settings, kickstartGenres]);

  const removeGenre = useCallback((g) => {
    const gs = (settings?.genres || []).filter((x) => x !== g);
    saveSettings({ genres: gs });
  }, [settings, saveSettings]);

  useEffect(() => { loadSuggested(); }, [loadSuggested, settings?.genres, sources.length]);

  const addSuggestedFeed = useCallback(async (feed) => {
    if (feed.already_added) return;
    try {
      await api.post("/news-ingestion/sources", {
        url: feed.url,
        label: feed.name,
        genre: feed.genre,
      });
      toast.success(`Added ${feed.name} (${feed.genre})`);
      const [srcRes] = await Promise.all([
        api.get("/news-ingestion/sources"),
        loadSuggested(),
      ]);
      setSources(srcRes.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add feed");
    }
  }, [loadSuggested]);

  // ── Script gen (with tone variants) ────────────────────────
  // Tones live on the backend at /news-ingestion/script-tones so
  // adding a new option there flows through automatically.
  const [scriptTones, setScriptTones] = useState([
    { id: "conversational", label: "Conversational" },
    { id: "breaking-alert", label: "Breaking Alert" },
    { id: "feature-story",  label: "Feature Story"  },
  ]);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/news-ingestion/script-tones");
        if (r.data.tones?.length) setScriptTones(r.data.tones);
      } catch { /* keep the hard-coded defaults */ }
    })();
  }, []);

  const generateScript = useCallback(async (item, tone = "conversational") => {
    // Open the modal immediately in "regenerating" state. When
    // switching tones we DIM the existing script rather than blowing
    // it away entirely so the user sees the transition.
    setScriptModal((prev) => (prev && prev.item?.item_id === item.item_id
      ? { ...prev, loading: true, activeTone: tone }
      : { item, loading: true, activeTone: tone }));
    try {
      const r = await api.post(
        `/news-ingestion/items/${item.item_id}/script`,
        { tone },
      );
      setScriptModal({
        item,
        loading: false,
        activeTone: tone,
        script: r.data.script,
        attribution: r.data.attribution,
        word_count: r.data.word_count,
        tone_label: r.data.tone_label,
      });
      toast.success(`Script drafted (${r.data.tone_label || "Conversational"})`);
    } catch (e) {
      setScriptModal(null);
      toast.error(e?.response?.data?.detail || "Script generation failed");
    }
  }, []);

  // ── Send current script to News Studio (creates bulletin + navigates) ─
  const sendToNewsStudio = useCallback(async () => {
    // The script row was persisted server-side when generateScript
    // fired — we just need to look up its script_id to hand off.
    if (!scriptModal?.script) return;
    setSendingToStudio(true);
    try {
      // Fetch the most recent script for this item + tone. Since
      // generateScript already persisted, the newest matching row
      // is the one we just made.
      const list = await api.get(
        `/news-ingestion/scripts?tone=${scriptModal.activeTone || "conversational"}&limit=5`,
      );
      const match = (list.data.items || []).find(
        (s) => s.item_id === scriptModal.item.item_id,
      );
      if (!match) {
        toast.error("Couldn't find the saved script — try Regenerate then Send");
        return;
      }
      const r = await api.post(
        `/news-ingestion/scripts/${match.script_id}/send-to-news-studio`,
      );
      toast.success("Sent to News Studio — opening…");
      setScriptModal(null);
      // Navigate to News Studio with the new bulletin_id so it
      // auto-opens on the Compose tab.
      navigate(r.data.redirect_url || `/dashboard/news-studio?bulletin=${r.data.bulletin_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send to News Studio failed");
    } finally {
      setSendingToStudio(false);
    }
  }, [scriptModal, navigate]);

  // ── Script History vault ──────────────────────────────────────
  const openHistory = useCallback(async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const qs = historyToneFilter ? `?tone=${historyToneFilter}&limit=20` : "?limit=20";
      const r = await api.get(`/news-ingestion/scripts${qs}`);
      setHistoryItems(r.data.items || []);
    } catch (e) {
      toast.error("Could not load script history");
    } finally {
      setHistoryLoading(false);
    }
  }, [historyToneFilter]);

  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const qs = historyToneFilter ? `?tone=${historyToneFilter}&limit=20` : "?limit=20";
      const r = await api.get(`/news-ingestion/scripts${qs}`);
      setHistoryItems(r.data.items || []);
    } catch { /* silent */ }
    finally { setHistoryLoading(false); }
  }, [historyToneFilter]);

  useEffect(() => {
    if (historyOpen) refreshHistory();
  }, [historyOpen, historyToneFilter, refreshHistory]);

  const reuseHistoryScript = useCallback((row) => {
    // Open the ScriptModal pre-populated with the saved script.
    // We don't have the full news_item in the items list (it may
    // have rolled off), so we synthesise a lightweight `item` from
    // the script row. That gives the modal enough to display.
    setScriptModal({
      item: {
        item_id: row.item_id,
        title:   row.headline,
        url:     row.source_url,
        source_name: row.source_name,
      },
      loading: false,
      activeTone:  row.tone || "conversational",
      script:      row.script,
      attribution: row.attribution,
      word_count:  row.word_count,
      tone_label:  row.tone_label,
      reused_from_history: true,
      history_script_id: row.script_id,
    });
    setHistoryOpen(false);
  }, []);

  const deleteHistoryScript = useCallback(async (script_id, e) => {
    if (e) e.stopPropagation();
    if (!await appConfirm("Delete this saved script?")) return;
    try {
      await api.delete(`/news-ingestion/scripts/${script_id}`);
      setHistoryItems((prev) => prev.filter((s) => s.script_id !== script_id));
      toast.success("Script deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  }, []);

  const lastRefreshedAgo = useMemo(() => {
    if (!lastRefresh) return "—";
    try {
      const then = new Date(lastRefresh).getTime();
      const secs = Math.max(1, Math.round((Date.now() - then) / 1000));
      if (secs < 60) return `${secs}s ago`;
      if (secs < 3600) return `${Math.round(secs / 60)} min ago`;
      if (secs < 86400) return `${Math.round(secs / 3600)} hr ago`;
      return new Date(lastRefresh).toLocaleDateString();
    } catch { return "recently"; }
  }, [lastRefresh]);

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin" style={{ color: T.primary }} size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-8" style={{ background: T.bg, color: T.text, fontFamily: "Inter, sans-serif" }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="rounded-lg p-2" style={{ background: `${T.primary}22`, border: `1px solid ${T.primary}55` }}>
            <Newspaper size={18} style={{ color: T.primary }} />
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: -0.5 }}>News Ingestion</h1>
          <span className="rounded px-1.5 py-0.5 ml-1"
                style={{ background: T.primary, color: "#000", fontSize: 10, fontWeight: 700 }}>
            NEW
          </span>
        </div>
        <p className="mb-6 max-w-3xl" style={{ color: T.muted, fontSize: 14, lineHeight: 1.5 }}>
          Configure your local news feed — combine a licensed news provider with any RSS sources you trust,
          then hand off headlines to Video News Studio for paraphrased on-air scripts.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* ── LEFT COLUMN: Configuration ─────────────────── */}
          <div className="lg:col-span-1 space-y-4">
            <MarketSettingsCard
              settings={settings}
              onChange={saveSettings}
              languages={langOptions}
              newCategory={newCategory}
              setNewCategory={setNewCategory}
              onAddCategory={addCategory}
              onRemoveCategory={removeCategory}
              newGenre={newGenre}
              setNewGenre={setNewGenre}
              onAddGenre={addGenre}
              onRemoveGenre={removeGenre}
              onPickGenre={(g) => kickstartGenres([g])}
              saveState={saveState}
              onApply={refreshNow}
              refreshing={refreshing}
            />
            <ProviderFeedCard
              settings={settings}
              providers={providers}
              onChange={saveSettings}
              saveState={saveState}
            />
            <CustomRssCard
              sources={sources}
              rssUrl={rssUrl}
              setRssUrl={setRssUrl}
              addingSource={addingSource}
              onAdd={addRssSource}
              onTogglePause={togglePause}
              onDelete={deleteSource}
            />
            <SuggestedGenreFeedsCard
              suggested={suggested}
              onAdd={addSuggestedFeed}
              onAddAll={() => kickstartGenres(settings?.genres || [])}
              activeGenres={settings?.genres || []}
            />
          </div>

          {/* ── RIGHT COLUMN: Unified Headline Feed ────────── */}
          <div className="lg:col-span-2 space-y-4">
            <HotTakesStrip
              hotTakes={hotTakes}
              onPublish={publishHotTake}
              onDismiss={dismissHotTake}
              publishing={hotPublishing}
              onHistory={() => navigate("/dashboard/hot-takes")}
            />
            <HeadlineFeedCard
              items={items}
              filter={filter}
              setFilter={setFilter}
              refreshing={refreshing}
              onRefresh={refreshNow}
              lastRefreshedAgo={lastRefreshedAgo}
              onGenerateScript={generateScript}
              onOpenHistory={openHistory}
              onShare={setShareItem}
            />
          </div>
        </div>

        {/* Share to channels modal */}
        {shareItem && (
          <ShareModal item={shareItem} onClose={() => setShareItem(null)} />
        )}

        {/* Script modal */}
        {scriptModal && (
          <ScriptModal
            data={scriptModal}
            tones={scriptTones}
            onRegenerate={(tone) => generateScript(scriptModal.item, tone)}
            onSendToStudio={sendToNewsStudio}
            sendingToStudio={sendingToStudio}
            onClose={() => setScriptModal(null)}
          />
        )}

        {/* Script History vault modal */}
        {historyOpen && (
          <ScriptHistoryModal
            items={historyItems}
            loading={historyLoading}
            toneFilter={historyToneFilter}
            setToneFilter={setHistoryToneFilter}
            tones={scriptTones}
            onReuse={reuseHistoryScript}
            onDelete={deleteHistoryScript}
            onClose={() => setHistoryOpen(false)}
          />
        )}
      </div>
    </div>
  );
}


// ── Genre Hot Takes strip (Update #182) ────────────────────────
// Renders at the top of the right column whenever 2+ genre-tagged
// sources cluster on the same story. Each card offers a one-click
// "Publish" (creates a News Story draft) and a small × dismiss.
function HotTakesStrip({ hotTakes, onPublish, onDismiss, publishing, onHistory }) {
  if (!hotTakes || hotTakes.length === 0) return null;
  return (
    <div
      className="rounded-xl p-3"
      style={{
        background: "linear-gradient(90deg, rgba(255,138,31,0.10), rgba(147,51,234,0.10))",
        border: `1px solid ${T.warning}44`,
      }}
      data-testid="hot-takes-strip"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Zap size={14} style={{ color: T.warning }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: T.text, letterSpacing: 0.2 }}>
            GENRE HOT TAKES
          </span>
          <span style={{ fontSize: 10, color: T.muted }}>
            · {hotTakes.length} cluster{hotTakes.length === 1 ? "" : "s"} spanning multiple publishers
          </span>
        </div>
        <button
          type="button"
          onClick={onHistory}
          data-testid="hot-takes-history-btn"
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 transition hover:opacity-80"
          style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.muted, fontSize: 10, fontWeight: 600 }}
        >
          <History size={11} /> Archive
        </button>
      </div>
      <div className="space-y-1.5">
        {hotTakes.map((h) => (
          <div
            key={h.hot_take_id}
            className="rounded-lg px-3 py-2 flex items-center gap-2"
            style={{ background: T.elevated, border: `1px solid ${T.border}` }}
            data-testid={`hot-take-row-${h.hot_take_id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span
                  className="rounded-full px-1.5 py-0.5"
                  style={{ background: `${T.purple}22`, color: T.purple, fontSize: 10, fontWeight: 600 }}
                >♫ {h.genre}</span>
                <span style={{ fontSize: 10, color: T.muted }}>
                  {h.source_count} sources · {(h.sources || []).slice(0, 3).join(" · ")}
                  {h.sources && h.sources.length > 3 ? " …" : ""}
                </span>
                {h.status === "auto_generated" && (
                  <span style={{ fontSize: 10, color: T.success, fontWeight: 600 }}>· auto-broadcast ✓</span>
                )}
              </div>
              <div style={{ fontSize: 13, fontWeight: 500, color: T.text }} className="truncate">
                {h.headline}
              </div>
            </div>
            {h.status === "auto_generated" ? (
              <span style={{ fontSize: 10, color: T.muted }}>Story #{(h.story_id || "").slice(-6)}</span>
            ) : (
              <button
                type="button"
                onClick={() => onPublish(h.hot_take_id)}
                disabled={publishing === h.hot_take_id}
                data-testid={`hot-take-publish-${h.hot_take_id}`}
                title="Promote to a News Story draft + generate the multi-format package"
                className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5"
                style={{
                  background: T.primary, color: "#000", fontSize: 11, fontWeight: 700,
                  opacity: publishing === h.hot_take_id ? 0.5 : 1,
                }}
              >
                {publishing === h.hot_take_id
                  ? <><Loader2 size={11} className="animate-spin" /> Publishing…</>
                  : <><Send size={11} /> Publish</>}
              </button>
            )}
            <button
              type="button"
              onClick={() => onDismiss(h.hot_take_id)}
              data-testid={`hot-take-dismiss-${h.hot_take_id}`}
              title="Dismiss — this cluster won't reappear"
              className="rounded p-1"
              style={{ background: "transparent", color: T.muted }}
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}



// ── Reusable collapsible card header (Update #181) ─────────────
// Every sidebar card wraps its title + subline in this so users can
// hide any section they don't care about. State lives in localStorage
// so the choice persists across navigation.
function SidebarCardHeader({ title, subline, collapsed, onToggle, testid, right }) {
  return (
    <div className="flex items-start gap-2" style={{ marginBottom: collapsed ? 0 : 14 }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        data-testid={testid}
        className="flex-1 flex items-start gap-2 text-left"
        style={{ background: "transparent", cursor: "pointer" }}
      >
        <div className="flex-1 min-w-0">
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 3, color: T.text }}>{title}</h2>
          {subline && <div style={{ fontSize: 12, color: T.muted }}>{subline}</div>}
        </div>
        <div className="rounded-md p-1 flex-shrink-0" style={{ color: T.muted, marginTop: 2 }}>
          {collapsed
            ? <ChevronDown size={14} />
            : <ChevronUp   size={14} />}
        </div>
      </button>
      {right && <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>{right}</div>}
    </div>
  );
}


// ── Market Settings card ───────────────────────────────────────
function SavedFlash({ state }) {
  if (!state) return null;
  const saved = state === "saved";
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5"
      data-testid="ni-saved-flash"
      style={{
        fontSize: 10, fontWeight: 600, marginTop: 2,
        background: saved ? "#10B98122" : `${T.muted}22`,
        color: saved ? "#10B981" : T.muted,
      }}
    >
      {saved ? <Check size={10} /> : <Loader2 size={10} className="animate-spin" />}
      {saved ? "Saved" : "Saving…"}
    </span>
  );
}


function MarketSettingsCard({
  settings, onChange, languages,
  newCategory, setNewCategory, onAddCategory, onRemoveCategory,
  newGenre, setNewGenre, onAddGenre, onRemoveGenre,
  onPickGenre, saveState,
  onApply, refreshing,
}) {
  const [collapsed, toggle] = useCollapsible("ni.market.collapsed");
  // Update #264 · Voice preview
  const [previewing, setPreviewing] = useState(false);
  const previewRef = useRef(null);
  const playPreview = async () => {
    if (previewRef.current) {
      previewRef.current.pause();
      previewRef.current = null;
      setPreviewing(false);
      return;
    }
    setPreviewing("loading");
    try {
      const voice = settings.hot_take_voice || "female_news";
      const style = settings.hot_take_voice_style || "standard";
      const r = await api.get(
        `/news-ingestion/hot-take-voices/${voice}/preview?style=${style}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      const a = new Audio(url);
      previewRef.current = a;
      a.onended = () => { setPreviewing(false); previewRef.current = null; URL.revokeObjectURL(url); };
      await a.play();
      setPreviewing("playing");
    } catch {
      toast.error("Preview failed — try again");
      setPreviewing(false);
      previewRef.current = null;
    }
  };
  return (
    <div className="rounded-xl p-4" style={{ background: T.card, border: `1px solid ${T.border}` }}>
      <SidebarCardHeader
        title="Market Settings"
        subline="Controls which provider headlines are pulled in"
        collapsed={collapsed}
        onToggle={toggle}
        testid="ni-market-toggle"
        right={<SavedFlash state={saveState} />}
      />
      {!collapsed && (
      <>
      <FieldLabel><Globe size={11} className="inline mr-1" style={{ marginTop: -1 }} />Country</FieldLabel>
      <select
        data-testid="news-country-select"
        value={settings.country}
        onChange={(e) => onChange({ country: e.target.value })}
        className="w-full rounded px-3 py-2 mb-3"
        style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 13 }}
      >
        {COUNTRIES.map((c) => (
          <option key={c.code} value={c.code}>{c.label}</option>
        ))}
      </select>

      <FieldLabel><Languages size={11} className="inline mr-1" style={{ marginTop: -1 }} />Primary Language</FieldLabel>
      <select
        data-testid="news-language-select"
        value={settings.language}
        onChange={(e) => onChange({ language: e.target.value })}
        className="w-full rounded px-3 py-2 mb-3"
        style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 13 }}
      >
        {(languages && languages.length ? languages : LANGUAGES).map((l) => (
          <option key={l.code} value={l.code}>{l.label}</option>
        ))}
      </select>

      <FieldLabel>Categories</FieldLabel>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(settings.categories || []).map((cat) => (
          <span
            key={cat}
            className="rounded-full px-2.5 py-1 inline-flex items-center gap-1"
            style={{
              background: `${T.primary}1a`, border: `1px solid ${T.primary}55`,
              color: T.primary, fontSize: 11, fontWeight: 500,
            }}
            data-testid={`news-category-${cat.toLowerCase()}`}
          >
            {cat}
            <button
              type="button"
              onClick={() => onRemoveCategory(cat)}
              className="hover:opacity-100 opacity-60"
              title="Remove"
              data-testid={`news-remove-category-${cat.toLowerCase()}`}
            >
              <X size={10} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          data-testid="news-new-category-input"
          type="text"
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAddCategory(); }}}
          placeholder="Add a category…"
          className="flex-1 rounded px-2 py-1.5"
          style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 12 }}
        />
        <button
          type="button"
          onClick={onAddCategory}
          disabled={!newCategory.trim()}
          data-testid="news-add-category-btn"
          className="rounded px-3 py-1.5 inline-flex items-center gap-1"
          style={{
            background: T.elevated, border: `1px solid ${T.border}`,
            color: T.text, fontSize: 12,
            opacity: newCategory.trim() ? 1 : 0.4,
          }}
        >
          <Plus size={12} /> Add
        </button>
      </div>

      {/* Update #180 · Genre tags — first-class filter, independent from categories */}
      <div style={{ marginTop: 16 }}>
        <FieldLabel>
          Genre <span style={{ color: T.muted, fontSize: 10, fontWeight: 400 }}>· music news</span>
        </FieldLabel>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {(settings.genres || []).length === 0 && (
            <span style={{ fontSize: 11, color: T.muted, fontStyle: "italic" }}>
              None yet — pick genres to unlock music-news filtering + suggested feeds.
            </span>
          )}
          {(settings.genres || []).map((g) => (
            <span
              key={g}
              className="rounded-full px-2.5 py-1 inline-flex items-center gap-1"
              style={{
                background: `${T.purple}22`, border: `1px solid ${T.purple}66`,
                color: T.purple, fontSize: 11, fontWeight: 500,
              }}
              data-testid={`news-genre-${g.toLowerCase()}`}
            >
              {g}
              <button
                type="button"
                onClick={() => onRemoveGenre(g)}
                className="hover:opacity-100 opacity-60"
                title="Remove"
                data-testid={`news-remove-genre-${g.toLowerCase()}`}
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
        {(settings.genres || []).length === 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {GENRE_SUGGESTIONS.slice(0, 8).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => {
                  const gs = settings.genres || [];
                  if (gs.some((x) => x.toLowerCase() === g.toLowerCase())) return;
                  onChange({ genres: [...gs, g] });
                }}
                data-testid={`news-suggest-genre-${g.toLowerCase()}`}
                className="rounded-full px-2 py-0.5 hover:opacity-100 opacity-70 transition"
                style={{
                  background: T.elevated, border: `1px dashed ${T.purple}55`,
                  color: T.purple, fontSize: 10, fontWeight: 500,
                }}
              >+ {g}</button>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            data-testid="news-new-genre-input"
            type="text"
            value={newGenre}
            onChange={(e) => setNewGenre(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAddGenre(); }}}
            placeholder="Add a music genre…"
            className="flex-1 rounded px-2 py-1.5"
            style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 12 }}
          />
          <button
            type="button"
            onClick={onAddGenre}
            disabled={!newGenre.trim()}
            data-testid="news-add-genre-btn"
            className="rounded px-3 py-1.5 inline-flex items-center gap-1"
            style={{
              background: T.elevated, border: `1px solid ${T.border}`,
              color: T.text, fontSize: 12,
              opacity: newGenre.trim() ? 1 : 0.4,
            }}
          >
            <Plus size={12} /> Add
          </button>
        </div>
      </div>

      {/* Update #182 · Hot Take auto-broadcast toggle */}
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
        <label className="flex items-start gap-2 cursor-pointer" data-testid="ni-hot-take-toggle">
          <input
            type="checkbox"
            checked={!!settings.hot_take_autobroadcast}
            onChange={(e) => onChange({ hot_take_autobroadcast: e.target.checked })}
            style={{ marginTop: 3, accentColor: T.warning }}
          />
          <div className="flex-1 min-w-0">
            <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }} className="flex items-center gap-1">
              <Zap size={12} style={{ color: T.warning }} />
              Genre Hot Take auto-broadcast
            </div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
              When 2+ genre-tagged sources cluster on the same story, auto-generate a
              News Story draft (radio_30 + blog + socials, text-only).
            </div>
          </div>
        </label>
        {/* Update #260 · Hot Take email alert (bell ping is always on) */}
        <label className="flex items-start gap-2 cursor-pointer" style={{ marginTop: 10 }} data-testid="ni-hot-take-email-toggle">
          <input
            type="checkbox"
            checked={!!settings.hot_take_alert_email}
            onChange={(e) => onChange({ hot_take_alert_email: e.target.checked })}
            style={{ marginTop: 3, accentColor: T.warning }}
          />
          <div className="flex-1 min-w-0">
            <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }} className="flex items-center gap-1">
              <Bell size={12} style={{ color: T.warning }} />
              Email me the moment a Hot Take fires
            </div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
              Dashboard bell ping is always on — this also sends an email so you can air it fast.
            </div>
          </div>
        </label>
        {/* Update #261 · Quiet hours */}
        <label className="flex items-start gap-2 cursor-pointer" style={{ marginTop: 10 }} data-testid="ni-quiet-hours-toggle">
          <input
            type="checkbox"
            checked={!!settings.hot_take_quiet_enabled}
            onChange={(e) => onChange({ hot_take_quiet_enabled: e.target.checked })}
            style={{ marginTop: 3, accentColor: T.accent }}
          />
          <div className="flex-1 min-w-0">
            <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }} className="flex items-center gap-1">
              <Moon size={12} style={{ color: T.accent }} />
              Quiet hours — hold overnight pings
            </div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
              Hot Takes detected in this window stay silent and roll into one morning summary.
            </div>
          </div>
        </label>
        {!!settings.hot_take_quiet_enabled && (
          <div className="flex items-center gap-2" style={{ marginLeft: 22, marginTop: 8 }} data-testid="ni-quiet-hours-row">
            <input
              type="time"
              value={settings.hot_take_quiet_start || "22:00"}
              onChange={(e) => onChange({ hot_take_quiet_start: e.target.value })}
              data-testid="ni-quiet-start"
              className="rounded px-2 py-1"
              style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 12 }}
            />
            <span style={{ color: T.muted, fontSize: 11 }}>→</span>
            <input
              type="time"
              value={settings.hot_take_quiet_end || "07:00"}
              onChange={(e) => onChange({ hot_take_quiet_end: e.target.value })}
              data-testid="ni-quiet-end"
              className="rounded px-2 py-1"
              style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 12 }}
            />
            <span style={{ color: T.muted, fontSize: 10 }}>station time</span>
          </div>
        )}
        {/* Update #263 · Announcer voice + read style for voice reads */}
        <div style={{ marginTop: 10 }} data-testid="ni-voice-choice">
          <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }} className="flex items-center gap-1">
            <Mic size={12} style={{ color: T.purple }} />
            Voice read — announcer &amp; style
          </div>
          <div className="flex items-center gap-2" style={{ marginTop: 6 }}>
            <select
              value={settings.hot_take_voice || "female_news"}
              onChange={(e) => onChange({ hot_take_voice: e.target.value })}
              data-testid="ni-voice-select"
              className="flex-1 rounded px-2 py-1.5"
              style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 12 }}
            >
              <option value="female_news">Female News Voice</option>
              <option value="male_news">Male News Voice</option>
              <option value="warm_news">Warm Caribbean Voice</option>
              <option value="energetic_news">Energetic Anchor</option>
            </select>
            <select
              value={settings.hot_take_voice_style || "standard"}
              onChange={(e) => onChange({ hot_take_voice_style: e.target.value })}
              data-testid="ni-voice-style-select"
              className="rounded px-2 py-1.5"
              style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 12 }}
            >
              <option value="relaxed">Relaxed</option>
              <option value="standard">Standard</option>
              <option value="upbeat">Upbeat</option>
            </select>
            <button
              type="button"
              onClick={playPreview}
              data-testid="ni-voice-preview-btn"
              title={previewing === "playing" ? "Stop preview" : "Hear this announcer"}
              className="shrink-0 rounded-full p-2 transition hover:opacity-80"
              style={{ background: `${T.purple}22`, border: `1px solid ${T.purple}66`, color: T.purple }}
            >
              {previewing === "loading"
                ? <Loader2 size={12} className="animate-spin" />
                : previewing === "playing" ? <Square size={12} /> : <Play size={12} />}
            </button>
          </div>
          <div style={{ fontSize: 10, color: T.muted, marginTop: 4 }}>
            Used for every Hot Take voice read — airing one generates broadcast-ready audio in this voice.
          </div>
        </div>
        {/* Update #189 · Hot Take → WordPress push */}
        {!!settings.hot_take_autobroadcast && (
          <div style={{ marginTop: 10, marginLeft: 22 }} data-testid="ni-hot-take-wp-row">
            <label className="flex items-start gap-2 cursor-pointer" data-testid="ni-hot-take-wp-toggle">
              <input
                type="checkbox"
                checked={!!settings.hot_take_wp_publish}
                onChange={(e) => onChange({ hot_take_wp_publish: e.target.checked })}
                style={{ marginTop: 3, accentColor: T.accent }}
              />
              <div className="flex-1 min-w-0">
                <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }} className="flex items-center gap-1">
                  <Globe size={12} style={{ color: T.accent }} />
                  Also push Hot Takes to WordPress
                </div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                  Sends the generated blog article straight to your connected WordPress site.
                </div>
              </div>
            </label>
            {!!settings.hot_take_wp_publish && (
              <div className="flex items-center gap-2" style={{ marginTop: 6, marginLeft: 22 }}>
                <span style={{ fontSize: 11, color: T.muted }}>Publish as</span>
                <select
                  value={settings.hot_take_wp_status || "draft"}
                  onChange={(e) => onChange({ hot_take_wp_status: e.target.value })}
                  data-testid="ni-hot-take-wp-status"
                  className="rounded px-2 py-1"
                  style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 11 }}
                >
                  <option value="draft">Draft (review first)</option>
                  <option value="publish">Publish immediately</option>
                </select>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Apply & refresh — settings auto-save, this pulls headlines that match them */}
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
        <button
          type="button"
          onClick={onApply}
          disabled={refreshing}
          data-testid="ni-market-apply-btn"
          className="w-full rounded px-3 py-2 inline-flex items-center justify-center gap-2 font-semibold transition hover:opacity-90"
          style={{
            background: T.primary, color: "#fff", fontSize: 12,
            opacity: refreshing ? 0.6 : 1,
          }}
        >
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Fetching headlines…" : "Apply & refresh headlines"}
        </button>
        <div style={{ fontSize: 10, color: T.muted, marginTop: 6, textAlign: "center" }}>
          Changes save automatically — this pulls fresh headlines matching them.
        </div>
      </div>
      </>
      )}
    </div>
  );
}


// ── Suggested Genre Feeds card (Update #180 · spec v2) ─────────
// Renders curated RSS feeds matching the station's active Genre tags.
// One-click "+" pipes into POST /news-ingestion/sources with genre
// pre-filled so downstream items inherit the genre tag automatically.
function SuggestedGenreFeedsCard({ suggested, onAdd, onAddAll, activeGenres }) {
  const notAdded = (suggested || []).filter((s) => !s.already_added);
  const [collapsed, toggle] = useCollapsible("ni.suggested.collapsed");
  return (
    <div className="rounded-xl p-4" style={{ background: T.card, border: `1px solid ${T.border}` }}>
      <SidebarCardHeader
        title="Suggested genre feeds"
        subline={activeGenres.length
          ? `Curated for ${activeGenres.join(" · ")}. One-click add.`
          : "Pick genres in Market Settings above to unlock a personalized list."}
        collapsed={collapsed}
        onToggle={toggle}
        testid="ni-suggested-toggle"
      />
      {!collapsed && (
      <>
      {(suggested || []).length === 0 ? (
        <div className="rounded-lg px-3 py-4 text-center" style={{ background: T.elevated, border: `1px dashed ${T.border}`, color: T.muted, fontSize: 12 }}>
          No suggested feeds for your active genres yet.
        </div>
      ) : (
        <div className="space-y-1.5">
          {suggested.map((f) => (
            <div
              key={f.url}
              className="rounded-lg px-3 py-2 flex items-center gap-2"
              style={{
                background: T.elevated,
                border: `1px solid ${f.already_added ? T.border : `${T.purple}44`}`,
                opacity: f.already_added ? 0.55 : 1,
              }}
              data-testid={`news-suggested-feed-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="flex-1 min-w-0">
                <div style={{ fontSize: 13, fontWeight: 500, color: T.text }} className="truncate">{f.name}</div>
                <div className="flex items-center gap-1.5 mt-0.5" style={{ fontSize: 10, color: T.muted }}>
                  <span
                    className="rounded-full px-1.5 py-0.5"
                    style={{ background: `${T.purple}22`, color: T.purple, fontWeight: 500 }}
                  >{f.genre}</span>
                  <SourceFavicon url={f.url} size={12} />
                  <span className="truncate">{f.url.replace(/^https?:\/\//, "").split("/")[0]}</span>
                </div>
              </div>
              {f.already_added ? (
                <span style={{ fontSize: 10, color: T.success, fontWeight: 500 }} className="inline-flex items-center gap-1">
                  <Check size={12} /> Added
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => onAdd(f)}
                  data-testid={`news-add-suggested-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  title={`Add ${f.name} to your Custom RSS feeds`}
                  className="rounded-md h-7 w-7 inline-flex items-center justify-center hover:opacity-100 opacity-90 transition"
                  style={{ background: T.purple, color: "#fff" }}
                >
                  <Plus size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {notAdded.length > 1 && activeGenres.length > 0 && (
        <button
          type="button"
          onClick={onAddAll}
          data-testid="ni-wire-all-feeds-btn"
          className="w-full rounded px-3 py-2 mt-2 inline-flex items-center justify-center gap-1.5 font-semibold transition hover:opacity-90"
          style={{ background: `${T.purple}22`, border: `1px solid ${T.purple}66`, color: T.purple, fontSize: 12 }}
        >
          <Zap size={12} /> Wire all {notAdded.length} feeds
        </button>
      )}
      {notAdded.length === 0 && suggested?.length > 0 && (
        <div style={{ fontSize: 11, color: T.muted, marginTop: 8, textAlign: "center" }}>
          All matching feeds added ✓
        </div>
      )}
      </>
      )}
    </div>
  );
}


// ── Provider Feed card ─────────────────────────────────────────
function ProviderFeedCard({ settings, providers, onChange, saveState }) {
  const nd = providers.find((p) => p.provider === "newsdata");
  const gn = providers.find((p) => p.provider === "gnews");
  const sa = providers.find((p) => p.provider === "serpapi");
  const [collapsed, toggle] = useCollapsible("ni.provider.collapsed");
  return (
    <div className="rounded-xl p-4" style={{ background: T.card, border: `1px solid ${T.border}` }}>
      <SidebarCardHeader
        title="Provider Feed"
        subline="Platform-tier headline aggregation"
        collapsed={collapsed}
        onToggle={toggle}
        testid="ni-provider-toggle"
        right={<SavedFlash state={saveState} />}
      />
      {!collapsed && (
      <>

      <ProviderRow
        label="NewsData.io (default)"
        sub={
          nd?.status === "platform" ? "Platform key active · 206 countries"
          : nd?.status === "byok"   ? "Your API key connected"
          : "Provider key not configured yet"
        }
        active={settings.provider_preference === "newsdata"}
        onClick={() => onChange({ provider_preference: "newsdata" })}
        badge={nd?.status === "platform" ? "READY" : nd?.status === "byok" ? "BYOK" : "OFFLINE"}
        badgeColor={nd?.status === "unavailable" ? T.faint : T.success}
        testid="news-provider-newsdata"
      />
      <ProviderRow
        label="SerpAPI (Google News)"
        sub={
          sa?.status === "platform" ? "Platform key active · Google News global"
          : sa?.status === "byok"   ? "Your API key connected"
          : "Provider key not configured yet"
        }
        active={settings.provider_preference === "serpapi"}
        onClick={() => onChange({ provider_preference: "serpapi" })}
        badge={sa?.status === "platform" ? "READY" : sa?.status === "byok" ? "BYOK" : "OFFLINE"}
        badgeColor={sa?.status === "unavailable" ? T.faint : T.success}
        testid="news-provider-serpapi"
      />
      <ProviderRow
        label="GNews"
        sub={
          gn?.status === "platform" ? "Platform key active · 60,000+ sources"
          : gn?.status === "byok"   ? "Your API key connected"
          : "Provider key not configured yet"
        }
        active={settings.provider_preference === "gnews"}
        onClick={() => onChange({ provider_preference: "gnews" })}
        badge={gn?.status === "platform" ? "READY" : gn?.status === "byok" ? "BYOK" : "NOT SET"}
        badgeColor={gn?.status === "unavailable" ? T.faint : T.success}
        testid="news-provider-gnews"
      />

      <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${T.border}` }}>
        <ToggleRow
          label="Auto-refresh"
          sub={`Pull new headlines every ${settings.refresh_interval_minutes} minutes`}
          checked={settings.auto_refresh_enabled}
          onChange={(v) => onChange({ auto_refresh_enabled: v })}
          testid="news-auto-refresh-toggle"
        />
        <div className="mt-2">
          <FieldLabel>Refresh interval (minutes, minimum 5)</FieldLabel>
          <input
            type="number"
            min={5}
            max={1440}
            step={5}
            value={settings.refresh_interval_minutes}
            onChange={(e) => {
              const v = Math.max(5, Math.min(1440, parseInt(e.target.value) || 30));
              onChange({ refresh_interval_minutes: v });
            }}
            className="w-full rounded px-3 py-1.5"
            style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 12 }}
            data-testid="news-refresh-interval-input"
          />
        </div>
      </div>
      </>
      )}
    </div>
  );
}


// ── Custom RSS card ────────────────────────────────────────────
function CustomRssCard({ sources, rssUrl, setRssUrl, addingSource, onAdd, onTogglePause, onDelete }) {
  const [collapsed, toggle] = useCollapsible("ni.customrss.collapsed");
  return (
    <div className="rounded-xl p-4" style={{ background: T.card, border: `1px solid ${T.border}` }}>
      <SidebarCardHeader
        title="Custom RSS Feeds"
        subline="Add hyperlocal sources your provider doesn't index"
        collapsed={collapsed}
        onToggle={toggle}
        testid="ni-customrss-toggle"
      />
      {!collapsed && (
      <>
      <div className="space-y-1.5 mb-3">
        {sources.length === 0 && (
          <div className="text-center rounded-lg py-6"
               style={{ background: T.elevated, border: `1px dashed ${T.border}`, color: T.muted, fontSize: 12 }}>
            <Rss size={16} className="inline mb-1" />
            <div>No RSS feeds yet. Add your first one below.</div>
          </div>
        )}
        {sources.map((src) => (
          <RssSourceRow
            key={src.source_id}
            source={src}
            onTogglePause={() => onTogglePause(src)}
            onDelete={() => onDelete(src)}
          />
        ))}
      </div>

      <div className="flex gap-2">
        <input
          data-testid="news-rss-url-input"
          type="text"
          value={rssUrl}
          onChange={(e) => setRssUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !addingSource) {
              e.preventDefault();
              onAdd();
            }
          }}
          placeholder="Paste RSS feed URL…"
          className="flex-1 rounded px-3 py-2"
          style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 12 }}
        />
        <button
          type="button"
          data-testid="news-add-rss-btn"
          onClick={onAdd}
          disabled={addingSource || !rssUrl.trim()}
          className="rounded px-4 py-2 inline-flex items-center gap-1"
          style={{
            background: T.primary,
            color: "#000",
            fontSize: 12, fontWeight: 600,
            opacity: (addingSource || !rssUrl.trim()) ? 0.5 : 1,
          }}
        >
          {addingSource
            ? <><Loader2 size={12} className="animate-spin" /> Validating…</>
            : "Add"}
        </button>
      </div>
      </>
      )}
    </div>
  );
}


function RssSourceRow({ source, onTogglePause, onDelete }) {
  const statusColor = source.status === "live" ? T.success
                    : source.status === "error" ? T.danger
                    : T.warning;
  const statusText  = source.status === "live"
    ? formatSourceMeta(source)
    : source.status === "error"
      ? `Feed error — ${source.error_message || "unknown"}`
      : "Paused by station admin";
  return (
    <div
      className="rounded-lg px-3 py-2 flex items-center gap-3"
      style={{ background: T.elevated, border: `1px solid ${T.border}` }}
      data-testid={`news-source-${source.source_id}`}
    >
      <span
        className="rounded-full flex-shrink-0"
        style={{
          width: 8, height: 8, background: statusColor,
          boxShadow: source.status === "live" ? `0 0 8px ${statusColor}88` : undefined,
        }}
      />
      <SourceFavicon url={source.feed_url} size={16} />
      <div className="flex-1 min-w-0">
        <div style={{ fontSize: 13, fontWeight: 500, color: T.text }} className="truncate">
          {source.label || source.feed_url}
        </div>
        <div style={{ fontSize: 11, color: T.muted }} className="truncate">
          {statusText}
        </div>
      </div>
      <button
        type="button"
        onClick={onTogglePause}
        title={source.paused ? "Resume" : "Pause"}
        data-testid={`news-source-toggle-${source.source_id}`}
        className="rounded p-1.5"
        style={{ background: "transparent", color: T.muted }}
      >
        {source.paused ? <Play size={12} /> : <Pause size={12} />}
      </button>
      <button
        type="button"
        onClick={onDelete}
        title="Delete feed"
        data-testid={`news-source-delete-${source.source_id}`}
        className="rounded p-1.5"
        style={{ background: "transparent", color: T.muted }}
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}


function formatSourceMeta(source) {
  if (!source.last_polled_at) return "Not polled yet";
  const t = new Date(source.last_polled_at).getTime();
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  const when = mins === 0 ? "just now"
             : mins < 60  ? `${mins} min ago`
             : `${Math.round(mins / 60)} hr ago`;
  return `Last pulled ${when} · ${source.last_item_count || 0} items`;
}


// ── Unified headline feed (right column) ─────────────────────────
function HeadlineFeedCard({ items, filter, setFilter, refreshing, onRefresh, lastRefreshedAgo, onGenerateScript, onOpenHistory, onShare }) {
  return (
    <div className="rounded-xl p-4" style={{ background: T.card, border: `1px solid ${T.border}` }}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="inline-flex items-center rounded-lg p-0.5"
             style={{ background: T.elevated, border: `1px solid ${T.border}` }}
             data-testid="news-filter-tabs">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                data-testid={`news-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className="rounded px-3 py-1.5 inline-flex items-center gap-1.5"
                style={{
                  background: active ? T.primary : "transparent",
                  color: active ? "#000" : T.muted,
                  fontSize: 12, fontWeight: active ? 600 : 500,
                }}
              >
                <f.icon size={12} />
                {f.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-1.5" style={{ color: T.muted, fontSize: 11 }}>
            <span className="rounded-full inline-block"
                  style={{
                    width: 6, height: 6, background: T.success,
                    boxShadow: `0 0 6px ${T.success}aa`,
                    animation: "pulse-live 2s infinite",
                  }} />
            <span>Live · updated {lastRefreshedAgo}</span>
          </div>
          <button
            type="button"
            data-testid="news-history-btn"
            onClick={onOpenHistory}
            className="rounded-full inline-flex items-center gap-1 px-3 py-1.5"
            style={{
              background: T.elevated, border: `1px solid ${T.border}`,
              color: T.text, fontSize: 11, fontWeight: 500,
            }}
            title="Script vault — reuse recently drafted scripts"
          >
            <History size={11} /> Script vault
          </button>
          <button
            type="button"
            data-testid="news-refresh-btn"
            onClick={onRefresh}
            disabled={refreshing}
            className="rounded-full inline-flex items-center gap-1 px-3 py-1.5"
            style={{
              background: T.elevated, border: `1px solid ${T.border}`,
              color: refreshing ? T.muted : T.text,
              fontSize: 11, fontWeight: 500,
            }}
          >
            <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh now"}
          </button>
        </div>
      </div>

      <div className="space-y-2" data-testid="news-headline-list">
        {items.length === 0 ? (
          <div className="text-center rounded-lg py-16"
               style={{ background: T.elevated, border: `1px dashed ${T.border}`, color: T.muted }}>
            <Newspaper size={24} className="inline mb-2" />
            <div style={{ fontSize: 14 }}>No headlines yet</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              Add an RSS source (left) or click <b>Refresh now</b> to pull provider headlines.
            </div>
          </div>
        ) : (
          items.map((item) => (
            <HeadlineRow
              key={item.item_id}
              item={item}
              onGenerateScript={() => onGenerateScript(item)}
              onShare={() => onShare(item)}
            />
          ))
        )}
      </div>

      <style>{`
        @keyframes pulse-live {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.5; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
}


function HeadlineRow({ item, onGenerateScript, onShare }) {
  const pubAgo = useMemo(() => {
    if (!item.published_at) return "recently";
    try {
      const t = new Date(item.published_at).getTime();
      const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
      if (mins < 60)  return `${mins} min ago`;
      if (mins < 1440) return `${Math.round(mins / 60)} hr ago`;
      return `${Math.round(mins / 1440)} d ago`;
    } catch { return "recently"; }
  }, [item.published_at]);

  return (
    <div
      className="rounded-lg px-3 py-3 flex items-start gap-3 group"
      style={{ background: T.elevated, border: `1px solid ${T.border}` }}
      data-testid={`news-item-${item.item_id}`}
    >
      <div
        className="flex-shrink-0 rounded-lg flex items-center justify-center"
        style={{
          width: 44, height: 44, background: `${T.primary}22`,
          border: `1px solid ${T.border}`, fontSize: 22,
        }}
      >
        {item.thumbnail
          ? <img src={item.thumbnail} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 7 }} />
          : (item.display_icon || "📰")}
      </div>
      <div className="flex-1 min-w-0">
        <div style={{ fontSize: 14, fontWeight: 500, color: T.text, lineHeight: 1.35 }}
             className="mb-1.5">
          {item.title}
        </div>
        <div className="flex items-center gap-2 flex-wrap" style={{ fontSize: 11, color: T.muted }}>
          {item.is_breaking && (
            <span className="rounded-full px-2 py-0.5" style={{
              background: T.danger, color: "#000", fontWeight: 700, fontSize: 10,
            }}>Breaking</span>
          )}
          <span className="rounded px-1.5 py-0.5 inline-flex items-center gap-1.5" style={{
            background: item.provider === "rss" ? `${T.purple}22` : `${T.primary}22`,
            color: item.provider === "rss" ? T.purple : T.primary,
            fontSize: 10, fontWeight: 500,
          }}
            data-testid={`news-item-source-badge-${item.item_id}`}
          >
            <SourceFavicon url={item.url} size={13} />
            {item.provider === "rss" ? "RSS" : "Provider"} · {item.source_name || item.provider}
          </span>
          {item.genre && (
            <span
              className="rounded px-1.5 py-0.5"
              style={{
                background: `${T.purple}33`, color: T.purple,
                fontSize: 10, fontWeight: 600, letterSpacing: 0.2,
              }}
              data-testid={`news-item-genre-pill-${item.genre.toLowerCase()}`}
              title={`Genre: ${item.genre}`}
            >
              ♫ {item.genre}
            </span>
          )}
          <span>·</span>
          <span>{pubAgo}</span>
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          data-testid={`news-item-script-${item.item_id}`}
          onClick={onGenerateScript}
          title="Generate on-air script (paraphrased)"
          className="rounded-full flex items-center justify-center"
          style={{
            width: 30, height: 30, background: T.primary, color: "#000",
          }}
        >
          <Pencil size={12} />
        </button>
        <button
          type="button"
          data-testid={`news-item-share-${item.item_id}`}
          onClick={onShare}
          title="Share to social media, WhatsApp, email…"
          className="rounded-full flex items-center justify-center"
          style={{
            width: 30, height: 30, background: T.elevated,
            border: `1px solid ${T.border}`, color: T.muted,
          }}
        >
          <Share2 size={11} />
        </button>
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`news-item-open-${item.item_id}`}
          title="Open source"
          className="rounded-full flex items-center justify-center"
          style={{
            width: 30, height: 30, background: T.elevated,
            border: `1px solid ${T.border}`, color: T.muted,
          }}
        >
          <ExternalLink size={11} />
        </a>
      </div>
    </div>
  );
}


// ── Toggle + FieldLabel primitives ─────────────────────────────
function FieldLabel({ children }) {
  return (
    <div className="mb-1" style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 500 }}>
      {children}
    </div>
  );
}


function ToggleRow({ label, sub, checked, onChange, testid }) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="flex-1 min-w-0">
        <div style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{sub}</div>}
      </div>
      <button
        type="button"
        data-testid={testid}
        onClick={() => onChange(!checked)}
        className="rounded-full flex-shrink-0"
        style={{
          width: 34, height: 20,
          background: checked ? T.success : T.faint,
          position: "relative",
          transition: "background 150ms",
        }}
      >
        <span
          className="rounded-full absolute"
          style={{
            width: 14, height: 14, background: "#0f1117",
            top: 3, left: checked ? 17 : 3,
            transition: "left 150ms",
          }}
        />
      </button>
    </div>
  );
}


function ProviderRow({ label, sub, active, onClick, badge, badgeColor, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="w-full rounded-lg px-3 py-2.5 flex items-center gap-3 text-left mb-1.5 transition"
      style={{
        background: active ? `${T.primary}1a` : T.elevated,
        border: `1px solid ${active ? T.primary : T.border}`,
      }}
    >
      <div className="rounded-full flex-shrink-0"
           style={{
             width: 12, height: 12,
             background: active ? T.primary : "transparent",
             border: `2px solid ${active ? T.primary : T.faint}`,
           }} />
      <div className="flex-1 min-w-0">
        <div style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{label}</div>
        <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }} className="truncate">{sub}</div>
      </div>
      {badge && (
        <span className="rounded px-1.5 py-0.5 flex-shrink-0"
              style={{
                background: `${badgeColor}22`, border: `1px solid ${badgeColor}55`,
                color: badgeColor, fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
              }}>
          {badge}
        </span>
      )}
    </button>
  );
}


// ── Script modal ───────────────────────────────────────────────
function ScriptModal({ data, tones, onRegenerate, onSendToStudio, sendingToStudio, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    if (!data.script) return;
    const text = `${data.script}\n\n${data.attribution || ""}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [data]);

  const activeTone = data.activeTone || "conversational";
  const hasScript = !!data.script;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ background: "rgba(0,0,0,0.7)" }}
      onClick={onClose}
      data-testid="news-script-modal"
    >
      <div
        className="rounded-xl w-full max-w-2xl"
        style={{ background: T.card, border: `1px solid ${T.border}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4"
             style={{ borderBottom: `1px solid ${T.border}` }}>
          <div className="flex-1 pr-3">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={14} style={{ color: T.primary }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: T.primary }}>
                AI-drafted on-air script
              </span>
              <span style={{ fontSize: 10, color: T.faint }}>· paraphrased for copyright safety</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 500, color: T.text }}>
              {data.item.title}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5"
            style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.muted }}
            data-testid="news-script-modal-close"
          >
            <X size={14} />
          </button>
        </div>

        {/* Tone selector — always visible so the user can pivot mid-modal */}
        <div className="px-5 pt-3 pb-1 flex items-center gap-2 flex-wrap"
             data-testid="news-script-tone-picker">
          <span style={{ fontSize: 11, color: T.muted, textTransform: "uppercase",
                         letterSpacing: 0.5, fontWeight: 500 }}>
            Tone
          </span>
          {tones.map((t) => {
            const active = activeTone === t.id;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`news-script-tone-${t.id}`}
                onClick={() => !data.loading && onRegenerate(t.id)}
                disabled={data.loading}
                className="rounded-full inline-flex items-center gap-1 px-3 py-1"
                style={{
                  background: active ? T.primary : T.elevated,
                  color: active ? "#000" : T.text,
                  border: `1px solid ${active ? T.primary : T.border}`,
                  fontSize: 11, fontWeight: active ? 600 : 500,
                  opacity: data.loading && !active ? 0.5 : 1,
                  cursor: data.loading ? "not-allowed" : "pointer",
                }}
              >
                {active && data.loading
                  ? <Loader2 size={10} className="animate-spin" />
                  : active
                    ? <Check size={10} />
                    : null}
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="px-5 py-4">
          {data.loading && !hasScript ? (
            <div className="text-center py-12" style={{ color: T.muted }}>
              <Loader2 className="animate-spin inline mb-2" size={20} style={{ color: T.primary }} />
              <div style={{ fontSize: 13 }}>Drafting an original paraphrase…</div>
              <div style={{ fontSize: 11, marginTop: 4 }}>Claude 4.5 is writing your on-air version</div>
            </div>
          ) : (
            <>
              <div
                className="rounded-lg px-4 py-3 mb-3 relative"
                style={{
                  background: T.elevated, border: `1px solid ${T.border}`,
                  fontSize: 13, lineHeight: 1.6, color: T.text, whiteSpace: "pre-wrap",
                  opacity: data.loading ? 0.4 : 1,
                  transition: "opacity 200ms",
                }}
                data-testid="news-script-body"
              >
                {data.script}
                {/* Regeneration overlay — keeps the current script
                    visible-but-dimmed while a new tone is loading so
                    the user sees the transition rather than a flash
                    of empty state. */}
                {data.loading && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-lg"
                       style={{ background: `${T.card}cc` }}>
                    <div className="flex items-center gap-2"
                         style={{ color: T.primary, fontSize: 12, fontWeight: 500 }}>
                      <Loader2 className="animate-spin" size={14} />
                      Rewriting for {tones.find((t) => t.id === activeTone)?.label || activeTone}…
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div style={{ fontSize: 11, color: T.muted }}>
                  <span>{data.word_count} words</span>
                  {data.tone_label && (
                    <>
                      <span className="mx-2" style={{ color: T.faint }}>·</span>
                      <span style={{ color: T.primary }}>{data.tone_label}</span>
                    </>
                  )}
                  <span className="mx-2" style={{ color: T.faint }}>·</span>
                  <span>{data.attribution}</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={copy}
                    disabled={data.loading}
                    data-testid="news-script-copy"
                    className="rounded px-3 py-1.5 inline-flex items-center gap-1"
                    style={{
                      background: T.elevated, border: `1px solid ${T.border}`,
                      color: T.text, fontSize: 12,
                      opacity: data.loading ? 0.4 : 1,
                    }}
                  >
                    {copied ? <><Check size={11} /> Copied</> : "Copy to clipboard"}
                  </button>
                  <button
                    type="button"
                    onClick={onSendToStudio}
                    disabled={data.loading || sendingToStudio}
                    data-testid="news-script-send-to-studio"
                    className="rounded px-3 py-1.5 inline-flex items-center gap-1"
                    style={{
                      background: T.purple, color: "#fff",
                      fontSize: 12, fontWeight: 600,
                      opacity: (data.loading || sendingToStudio) ? 0.5 : 1,
                    }}
                    title="Create a News Studio bulletin from this script and open it"
                  >
                    {sendingToStudio
                      ? <><Loader2 size={11} className="animate-spin" /> Sending…</>
                      : <><Send size={11} /> Send to News Studio</>}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    data-testid="news-script-done"
                    className="rounded px-4 py-1.5"
                    style={{ background: T.primary, color: "#000", fontSize: 12, fontWeight: 600 }}
                  >
                    Done
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Script History (vault) modal ────────────────────────────────
// Lists the last 20 AI-drafted scripts for this station. Filters by
// tone. Clicking a row loads it into the ScriptModal without an LLM
// call, so DJs can quickly grab yesterday's Breaking Alert version
// instead of regenerating.
function ScriptHistoryModal({ items, loading, toneFilter, setToneFilter, tones, onReuse, onDelete, onClose }) {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ background: "rgba(0,0,0,0.7)" }}
      onClick={onClose}
      data-testid="news-history-modal"
    >
      <div
        className="rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        style={{ background: T.card, border: `1px solid ${T.border}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0"
             style={{ borderBottom: `1px solid ${T.border}` }}>
          <div className="flex items-center gap-2">
            <History size={16} style={{ color: T.primary }} />
            <h2 style={{ fontSize: 16, fontWeight: 600, color: T.text }}>
              Script vault
            </h2>
            <span style={{ fontSize: 11, color: T.muted }}>
              · latest {items.length} draft{items.length === 1 ? "" : "s"}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5"
            style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.muted }}
            data-testid="news-history-close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-5 pt-3 pb-2 flex items-center gap-2 flex-wrap flex-shrink-0">
          <span style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 500 }}>
            Filter
          </span>
          <button
            type="button"
            onClick={() => setToneFilter("")}
            data-testid="news-history-filter-all"
            className="rounded-full px-3 py-1"
            style={{
              background: toneFilter === "" ? T.primary : T.elevated,
              color: toneFilter === "" ? "#000" : T.text,
              border: `1px solid ${toneFilter === "" ? T.primary : T.border}`,
              fontSize: 11, fontWeight: toneFilter === "" ? 600 : 500,
            }}
          >
            All
          </button>
          {tones.map((t) => {
            const active = toneFilter === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setToneFilter(t.id)}
                data-testid={`news-history-filter-${t.id}`}
                className="rounded-full px-3 py-1"
                style={{
                  background: active ? T.primary : T.elevated,
                  color: active ? "#000" : T.text,
                  border: `1px solid ${active ? T.primary : T.border}`,
                  fontSize: 11, fontWeight: active ? 600 : 500,
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="px-5 py-3 overflow-y-auto space-y-2 flex-1"
             data-testid="news-history-list">
          {loading ? (
            <div className="text-center py-16" style={{ color: T.muted }}>
              <Loader2 size={18} className="animate-spin inline mb-1" style={{ color: T.primary }} />
              <div style={{ fontSize: 12, marginTop: 6 }}>Loading vault…</div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center rounded-lg py-16"
                 style={{ background: T.elevated, border: `1px dashed ${T.border}`, color: T.muted }}>
              <Sparkles size={20} className="inline mb-2" />
              <div style={{ fontSize: 13 }}>
                {toneFilter
                  ? `No ${(tones.find((t) => t.id === toneFilter) || {}).label || toneFilter} scripts yet`
                  : "No AI-drafted scripts yet"}
              </div>
              <div style={{ fontSize: 11, marginTop: 4 }}>
                Click the pencil on any headline to draft one.
              </div>
            </div>
          ) : (
            items.map((s) => (
              <HistoryRow
                key={s.script_id}
                script={s}
                onReuse={() => onReuse(s)}
                onDelete={(e) => onDelete(s.script_id, e)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}


function HistoryRow({ script, onReuse, onDelete }) {
  const ago = useMemo(() => {
    if (!script.created_at) return "recently";
    try {
      const t = new Date(script.created_at).getTime();
      const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
      if (mins < 60) return `${mins}m ago`;
      if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
      return new Date(script.created_at).toLocaleDateString();
    } catch { return "recently"; }
  }, [script.created_at]);
  const preview = (script.script || "").slice(0, 140) + (script.script?.length > 140 ? "…" : "");
  return (
    <div
      className="rounded-lg px-3 py-3 flex items-start gap-3 group cursor-pointer"
      style={{ background: T.elevated, border: `1px solid ${T.border}` }}
      onClick={onReuse}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onReuse(); }}}
      data-testid={`news-history-row-${script.script_id}`}
    >
      <div className="rounded p-2 flex-shrink-0"
           style={{ background: `${T.primary}22`, border: `1px solid ${T.border}` }}>
        <Sparkles size={12} style={{ color: T.primary }} />
      </div>
      <div className="flex-1 min-w-0">
        <div style={{ fontSize: 13, fontWeight: 500, color: T.text, lineHeight: 1.35 }}
             className="mb-1 truncate">
          {script.headline || "Untitled headline"}
        </div>
        <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.4 }}
             className="line-clamp-2 mb-1.5">
          {preview}
        </div>
        <div className="flex items-center gap-2 flex-wrap" style={{ fontSize: 10, color: T.faint }}>
          {script.tone_label && (
            <span className="rounded px-1.5 py-0.5"
                  style={{
                    background: `${T.primary}22`,
                    color: T.primary, fontSize: 9, fontWeight: 600,
                  }}>
              {script.tone_label}
            </span>
          )}
          <span>{script.word_count || 0} words</span>
          <span style={{ color: T.faint }}>·</span>
          <span>{script.source_name || script.provider || "source"}</span>
          <span style={{ color: T.faint }}>·</span>
          <span>{ago}</span>
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onReuse(); }}
          data-testid={`news-history-reuse-${script.script_id}`}
          className="rounded-full inline-flex items-center gap-1 px-2.5 py-1"
          style={{ background: T.primary, color: "#000", fontSize: 11, fontWeight: 600 }}
        >
          Reuse <ArrowRight size={10} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          data-testid={`news-history-delete-${script.script_id}`}
          className="rounded-full inline-flex items-center justify-center"
          title="Delete script"
          style={{
            width: 26, height: 26,
            background: T.elevated, border: `1px solid ${T.border}`, color: T.danger,
          }}
        >
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  );
}



// ── Share to channels modal ─────────────────────────────────────
// Direct-send: facebook / instagram (live Meta) + email (newsletter).
// Quick-share: WhatsApp / X open with the caption + link pre-filled.
const DIRECT_SHARE_PLATFORMS = ["facebook", "instagram", "email"];

function ShareModal({ item, onClose }) {
  const [caption, setCaption] = useState(item.title || "");
  const [selected, setSelected] = useState(["facebook"]);
  const [sending, setSending] = useState(false);
  const [captionBusy, setCaptionBusy] = useState(false);
  const [results, setResults] = useState(null);
  const hasImage = (item.thumbnail || "").startsWith("http");
  const shareText = `${caption}\n\n${item.url || ""}`.trim();

  const toggle = (p) => setSelected((arr) => (arr.includes(p) ? arr.filter((x) => x !== p) : [...arr, p]));

  const aiCaption = async () => {
    setCaptionBusy(true);
    try {
      const r = await api.post(`/news-ingestion/items/${item.item_id}/caption`);
      setCaption(r.data.caption);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caption generation failed");
    } finally { setCaptionBusy(false); }
  };

  const sendNow = async () => {
    if (selected.length === 0) { toast.error("Pick at least one platform"); return; }
    setSending(true);
    setResults(null);
    try {
      const r = await api.post(`/news-ingestion/items/${item.item_id}/share`, {
        platforms: selected, caption,
      });
      const res = r.data.results || {};
      setResults(res);
      const ok = Object.entries(res).filter(([, v]) => v.ok).map(([k]) => k);
      const bad = Object.entries(res).filter(([, v]) => !v.ok);
      if (ok.length) toast.success(`Sent to ${ok.join(", ")}`);
      bad.forEach(([k, v]) => toast.error(`${k}: ${v.error}`, { duration: 8000 }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Share failed");
    } finally { setSending(false); }
  };

  const copyText = () => navigator.clipboard.writeText(shareText).then(
    () => toast.success("Copied to clipboard"),
    () => toast.error("Clipboard blocked"),
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-bg-surface border-line text-ink-primary max-w-lg overflow-hidden" data-testid="news-share-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <Share2 size={16} className="text-brand-amber" /> Share this story
          </DialogTitle>
          <DialogDescription className="text-ink-secondary text-xs line-clamp-2">{item.title}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 min-w-0">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Caption</span>
              <button onClick={aiCaption} disabled={captionBusy} data-testid="news-share-ai-caption"
                className="inline-flex items-center gap-1 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-2 py-0.5 text-[10px] font-semibold text-brand-amber hover:bg-brand-amber/20 disabled:opacity-50 transition">
                {captionBusy ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} AI caption
              </button>
            </div>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={4}
              data-testid="news-share-caption"
              className="w-full rounded-md border border-line bg-bg p-2.5 text-xs text-ink-primary focus:outline-none focus:border-brand-amber/60"
            />
            <div className="text-[10px] text-ink-muted mt-0.5 truncate">Link added automatically: {item.url}</div>
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1.5">Auto-post to</div>
            <div className="flex items-center gap-1.5">
              {DIRECT_SHARE_PLATFORMS.map((p) => (
                <PlatformToggle key={p} platform={p} on={selected.includes(p)}
                  onClick={() => toggle(p)} testid={`news-share-plat-${p}`} />
              ))}
            </div>
            {selected.includes("instagram") && !hasImage && (
              <div className="text-[10px] text-amber-300 mt-1.5">⚠ Instagram needs an image — this headline has none, so it will be skipped.</div>
            )}
            {selected.includes("email") && (
              <div className="text-[10px] text-ink-muted mt-1.5">Email blasts to your Newsletter subscribers.</div>
            )}
            {results && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="news-share-results">
                {Object.entries(results).map(([k, v]) => (
                  <span key={k} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono ${v.ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-rose-500/40 bg-rose-500/10 text-rose-300"}`}>
                    <PlatformIcon platform={k} className="h-3 w-3" /> {v.ok ? (v.external_url ? "posted" : `sent ${v.sent}/${v.total}`) : "failed"}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1.5">Quick share</div>
            <div className="flex items-center gap-2">
              <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer"
                data-testid="news-share-whatsapp"
                className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] text-ink-secondary hover:text-ink-primary transition"
                style={{ borderColor: `${getPlatformMeta("whatsapp").color}55` }}>
                <PlatformIcon platform="whatsapp" className="h-3.5 w-3.5" style={{ color: getPlatformMeta("whatsapp").color }} /> WhatsApp
              </a>
              <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(item.url || "")}`}
                target="_blank" rel="noopener noreferrer" data-testid="news-share-x"
                className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] text-ink-secondary hover:text-ink-primary transition">
                <PlatformIcon platform="x" className="h-3.5 w-3.5" /> Post on X
              </a>
              <button onClick={copyText} data-testid="news-share-copy"
                className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] text-ink-secondary hover:text-ink-primary transition">
                <CopyIcon size={12} /> Copy
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} data-testid="news-share-close"
              className="rounded-md border border-line bg-bg-elevated px-4 py-2 text-xs text-ink-secondary hover:text-ink-primary transition">Close</button>
            <button onClick={sendNow} disabled={sending || selected.length === 0}
              data-testid="news-share-send"
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-bold text-black disabled:opacity-40 transition">
              {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {sending ? "Sending…" : `Send to ${selected.length} channel${selected.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

